import { colorsPalettes } from "@similarweb/styles";
import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import dayjs from "dayjs";
import { abbrNumberVisitsFilter, minVisitsAbbrFilter, percentageSignFilter, timeFilter } from "app/filters/ngFilters";
import yAxisLabelsConfig from "pro-features/components/Chart/src/configs/yAxis/yAxisLabelsConfig";
import type { IDurationData } from "app/services/DurationService";
import type { TransformedDataTypes } from "./types";
import type { DashboardMetric } from "app/components/widget/widget-types/segments/type";
import { Y_AXIS_PERCENTS, Y_AXIS_TIME } from "app/components/widget/widget-types/segments/type";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { formatTooltipPointWithConfidence } from "pro-features/components/Chart/src/data/confidenceProcessor";
import {
    SegmentSubtitle,
    SegmentTypeBadge,
} from "pro-features/pages/segments/components/benchmarkOvertime/SegmentsVsGroupLineChart";
import { getTooltipHeaderElement } from "app/UtilitiesAndConstants/UtilitiesComponents/chartTooltipHeaderWithMTDSupport";
import { swSettings } from "app/common/services/swSettings";
import styled from "styled-components";
import { ChangeTooltip } from "@similarweb/ui-components";
import ReactDOMServer from "react-dom/server";
import get from "lodash/get";
import * as filters from "app/filters/ngFilters";
import { tickIntervals } from "app/components/widget/widget-types/GraphWidget";

const getFilter = (name: string) => filters[`${name}Filter`];

export const getChartConfig = (
    transformedData: TransformedDataTypes,
    durationApi: IDurationData,
    timeGranularity: GranularityNames,
    type: string,
    format: string,
    currentSegmentsData: unknown,
    currentGranularity: string,
    metric: DashboardMetric,
    isSegmentsGroup: boolean,
    filterFormatName: string,
) => {
    const isTurboThreshold = timeGranularity === "Daily";

    const xAxisTickInterval = tickIntervals[timeGranularity?.toLocaleLowerCase()];
    let yAxisFormatter = ({ value }) => abbrNumberVisitsFilter()(value);

    if (Y_AXIS_PERCENTS.includes(metric)) {
        yAxisFormatter = ({ value }) => percentageSignFilter()(value);
    }

    if (Y_AXIS_TIME.includes(metric)) {
        yAxisFormatter = ({ value }) => timeFilter()(value, null);
    }

    const xAxisFormatter = ({ value }) => dayjs.utc(value).utc().format(format);

    const plotOptions = {
        area: {
            fillColor: "rgba(220,220,249,0.1)",
            lineWidth: 1,
            lineColor: colorsPalettes.blue[400],
            connectNulls: false,
        },
        line: {
            fillColor: "rgba(220,220,249,0.1)",
            lineWidth: 2,
            lineColor: colorsPalettes.blue[400],
            connectNulls: false,
        },
        series: {
            name: currentSegmentsData[0]?.segmentName,
            connectNulls: false,
            stickyTracking: true,
            color: colorsPalettes.blue[400],
            turboThreshold: isTurboThreshold ? 1000 : null,
        },
    };

    const tooltipFilter = getFilter(filterFormatName);

    return combineConfigs(
        {
            isWindow: durationApi?.forAPI?.isWindow,
            windowToMoment: durationApi?.raw?.to,
            type,
            granularity: timeGranularity,
            yAxisFormatter,
            xAxisFormatter,
            data: transformedData,
            name: currentSegmentsData[0].segmentName,
            minVisitsAbbrFilter,
            showSeriesBulletColor: false,
        },
        [
            currentGranularity,
            yAxisLabelsConfig,
            getChangeTooltip(tooltipFilter, timeGranularity, durationApi?.raw?.to),
            {
                legend: {
                    enabled: false,
                },
                plotOptions: !isSegmentsGroup ? plotOptions : {},
                yAxis: {
                    gridLineWidth: 0.5,
                    showFirstLabel: true,
                    showLastLabel: true,
                    reversed: false,
                    gridZIndex: 2,
                    reversedStacks: true,
                    tickPixelInterval: 50,
                    labels: {
                        style: {
                            textTransform: "uppercase",
                            fontSize: "11px",
                            color: "#919191",
                        },
                    },
                },
                xAxis: {
                    crosshair: {
                        color: "#18A3FC",
                        dashStyle: "dash",
                        width: 1,
                        zIndex: 2,
                    },
                    gridLineWidth: 0,
                    gridLineDashStyle: "dash",
                    tickLength: 5,
                    labels: {
                        style: {
                            textTransform: "capitalize",
                            fontSize: "11px",
                            color: "#919191",
                        },
                    },
                    tickInterval: xAxisTickInterval,
                    minPadding: 0,
                    maxPadding: 0,
                },
            },
        ],
    );
};

const TooltipWrapper = styled.div`
    white-space: normal;
    padding: 5px;
`;

const getChangeTooltip = (
    filter: () => void,
    timeGranularity = GranularityNames.MONTHLY,
    toDateMoment = undefined,
) => ({
    tooltip: {
        shared: true,
        crosshairs: true,
        outside: true,
        useHTML: true,
        followPointer: false,
        formatter() {
            const changeTooltipProp = () =>
                this.points.map((row) => {
                    const isWebsite = row.series?.userOptions?.isWebsite;

                    return {
                        displayName: row.series?.userOptions?.displayName,
                        value: formatTooltipPointWithConfidence(row.y, get(row, "[point][confidence]", undefined), [
                            filter,
                        ]),
                        color: row.color,
                        subtitle: isWebsite ? (
                            <SegmentTypeBadge>WEBSITE</SegmentTypeBadge>
                        ) : (
                            <SegmentSubtitle>{row.series.name}</SegmentSubtitle>
                        ),
                    };
                });

            const changeTooltipProps = {
                rowHeight: 34,
                header: getTooltipHeaderElement(
                    timeGranularity,
                    this.x,
                    swSettings.current.lastSupportedDailyDate.isAfter(toDateMoment)
                        ? swSettings.current.lastSupportedDailyDate
                        : toDateMoment,
                ),
                tableHeaders: [
                    { position: 1, displayName: "" },
                    { position: 0, displayName: "" },
                ],
                tableRows: changeTooltipProp(),
                showChangeColumn: false,
            };

            return ReactDOMServer.renderToString(
                <TooltipWrapper>
                    <ChangeTooltip {...changeTooltipProps} />
                </TooltipWrapper>,
            );
        },
    },
});
